import { observer } from "mobx-react-lite";
import { useState } from "react";
import * as yup from 'yup';
import { Form as FormikForm, Formik, Field } from "formik";
import { Col, Form, Modal, Row } from "react-bootstrap";
import GradientButton from "../../../Core/GradientButton";
import { useSessionStore } from "../../../../Stores/SessionStore";
import AlertModal from "../../../Core/AlertModal";

interface ChangeWiFiPasswordModalProps {
    show: boolean;
    onClose: () => void;
}

interface AlertModalState {
    show: boolean;
    success: boolean;
    message: string;
    onClose: () => void;
}

const ChangeWiFiPasswordModal = observer(function ChangeWiFiPasswordModal(props: ChangeWiFiPasswordModalProps) {
    const store = useSessionStore();
    
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [successModal, setSuccessModal] = useState<AlertModalState | null>(null);

    async function handleSubmit(data: { NewWifiName: string; NewPassword: string; NewPasswordConfirmation: string }) {
        setSaving(true);
        setErrorMessage(null);
        
        var result = await store.CustomersService.UpdateWIFIPassword(data.NewWifiName, data.NewPassword, data.NewPasswordConfirmation);
        
        if(result && result.Success) {
            setSaving(false);
            setSuccessModal({
                show: true,
                success: true,
                message: "WiFi Name and Password changed successfully.",
                onClose: () => {
                    setSuccessModal(null);
                    props.onClose();
                }
            });
        } else {
            setSaving(false);
            setErrorMessage(result.Message);
        }
    }

    const validationSchema =
        yup.object({
            NewWifiName: yup.string().required("New WiFi Name is required.").min(6, "New WiFi Name must be at least 6 characters.").max(32, "New WiFi Name must be at most 32 characters.").test({
                test: (value: string | undefined) => !(/[^\w-]/.test(value || '')),
                message: "New WiFi Name must not contain special characters or spaces (dashes are allowed)."
            }),
            NewPassword: yup.string().required("New Password is required.").min(8, "New Password must be at least 8 characters."),
            NewPasswordConfirmation: yup.string().when('NewPassword', (NewPassword, schema) => {
                return schema.test({
                    test: (ConfirmNewPassword: string) => NewPassword === ConfirmNewPassword,
                    message: "Password Confirmation should match New Password"
                })})}
        ).required();

    return 	<Modal  size="lg" show={ props.show }>
                <Formik validationSchema={validationSchema} initialValues={{ NewWifiName: "", NewPassword: "", NewPasswordConfirmation: "" }} onSubmit={ handleSubmit}>
                {({ errors, touched, submitForm }) => (
                    <FormikForm>
                        <Modal.Header>
                            <h3>
                                Change WiFi Password
                            </h3>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Form.Group as={ Row }>
                                    <Form.Label column>New WiFi Name</Form.Label>
                                    <Col sm={8}>
                                        <Field 
                                            as={Form.Control} 
                                            name="NewWifiName"
                                            disabled={ store.LoggingIn } 
                                            isInvalid={errors.NewWifiName && touched.NewWifiName}                                                
                                        />
                                        <small className="text-danger">{touched.NewWifiName && errors.NewWifiName}</small>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={ Row } className="mt-3">
                                    <Form.Label column>New WiFi Password</Form.Label>
                                    <Col sm={8}>
                                        <Field 
                                            as={Form.Control} 
                                            name="NewPassword" 
                                            disabled={ store.LoggingIn } 
                                            isInvalid={errors.NewPassword && touched.NewPassword}                                                
                                        />
                                        <small className="text-danger">{touched.NewPassword && errors.NewPassword}</small>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={ Row } className="mt-3">
                                    <Form.Label column>Confirm WiFi Password</Form.Label>
                                    <Col sm={8}>
                                        <Field 
                                            as={Form.Control} 
                                            name="NewPasswordConfirmation" 
                                            disabled={ store.LoggingIn } 
                                            isInvalid={errors.NewPasswordConfirmation && touched.NewPasswordConfirmation}
                                        />
                                        <small className="text-danger">{touched.NewPasswordConfirmation && errors.NewPasswordConfirmation}</small>
                                    </Col>
                                </Form.Group>
                                <Row className="justify-content-center align-items-center mt-3">
                                    <p className="text-success text-center">Please keep your password safe.</p>
                                </Row>
                            </Form>
                            {
                                errorMessage &&
                                <p className="text-danger text-center">{errorMessage}</p>
                            }
                        </Modal.Body>
                        <Modal.Footer className="d-block">
                            <Row className="d-flex justify-content-around">
                                <Col className="text-center col-4">
                                    <GradientButton title={saving ? "Saving..." : "Save"} variant="greenYellow" type="submit" disabled={saving} loading={saving} />
                                </Col>
                                <Col className="text-center col-4">
                                    <GradientButton title="Cancel" variant="yellowOrange" onClick={props.onClose} />
                                </Col>
                            </Row>
                        </Modal.Footer>
                    </FormikForm>
                )}
                </Formik>
                { successModal && <AlertModal modal={successModal} /> }
            </Modal>
});

export default ChangeWiFiPasswordModal;